/**
 * Time on a board: the game's own clock, which stops while something covers the board, the loop
 * that moves things once a frame, and a seeded random so a board dealt once stays dealt.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Lang } from '../../../ui/text.js';

/** English or bokmål, inline. */
export const say = (lang: Lang, en: string, no: string) => (lang === 'no' ? no : en);

/**
 * The game's own clock in milliseconds, which stops while `paused` and carries on from where it
 * stood — so a light that was half way down is still half way down after an alarm. Pair it with
 * `useFrame` to redraw every animation frame.
 */
export function useGameTime(paused: boolean): () => number {
  const stoppedAt = useRef<number | null>(paused ? performance.now() : null);
  const lost = useRef(0);
  useEffect(() => {
    if (paused && stoppedAt.current === null) stoppedAt.current = performance.now();
    if (!paused && stoppedAt.current !== null) {
      lost.current += performance.now() - stoppedAt.current;
      stoppedAt.current = null;
    }
  }, [paused]);
  return useCallback(() => (stoppedAt.current ?? performance.now()) - lost.current, []);
}

/** Redraws the component on every animation frame while `running`. */
export function useFrame(running = true): void {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!running) return;
    let id = requestAnimationFrame(function step() {
      tick((n) => (n + 1) % 1_000_000);
      id = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(id);
  }, [running]);
}

/**
 * The game loop: runs `step(dt, now)` once per animation frame while not `paused` — `dt` in
 * seconds (capped, so a sleeping phone does not jump), `now` on the game's own clock — then
 * redraws. Keep movement here, in refs, and never in the render itself (React may render twice).
 * Returns the game's clock, for stamping taps.
 */
export function useTicker(paused: boolean, step: (dt: number, now: number) => void): () => number {
  const time = useGameTime(paused);
  const stepRef = useRef(step);
  stepRef.current = step;
  const hold = useRef(paused);
  hold.current = paused;
  const [, tick] = useState(0);
  useEffect(() => {
    let last = time();
    let id = requestAnimationFrame(function frame() {
      const now = time();
      const dt = Math.max(0, Math.min(0.05, (now - last) / 1000));
      last = now;
      if (!hold.current) stepRef.current(dt, now);
      tick((n) => (n + 1) % 1_000_000);
      id = requestAnimationFrame(frame);
    });
    return () => cancelAnimationFrame(id);
  }, [time]);
  return time;
}

/** A seeded random, so a board dealt once stays the same board through a re-render. */
export function seeded(seed: number): () => number {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}
